import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

interface SpecificationFieldDefinition {
  id: string;
  field_name: string;
  field_label: string;
  field_type: string;
  is_required: boolean;
  help_text?: string;
  unit_of_measure?: string;
  validation_rules?: {
    min?: number;
    max?: number;
    step?: number;
    allowed_values?: string[];
    min_date?: string;
    max_date?: string;
  };
}

interface SpecificationFieldInputProps {
  field: SpecificationFieldDefinition;
  value: any;
  onChange: (value: any) => void;
}

export function SpecificationFieldInput({ field, value, onChange }: SpecificationFieldInputProps) {
  const renderField = () => {
    switch (field.field_type) {
      case 'text':
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.help_text}
          />
        );
      
      case 'textarea':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.help_text}
            rows={3}
          />
        );
      
      case 'number':
        return (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={field.help_text}
              min={field.validation_rules?.min}
              max={field.validation_rules?.max}
              step={field.validation_rules?.step || 1}
              className="flex-1"
            />
            {field.unit_of_measure && (
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {field.unit_of_measure}
              </span>
            )}
          </div>
        );
      
      case 'dropdown':
        const options = field.validation_rules?.allowed_values || [];
        return (
          <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {options.map(option => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'multiselect':
        const multiselectOptions = field.validation_rules?.allowed_values || [];
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {multiselectOptions.map(option => (
              <div key={option} className="flex items-center gap-2">
                <Checkbox
                  id={`${field.id}-${option}`}
                  checked={selectedValues.includes(option)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      onChange([...selectedValues, option]);
                    } else {
                      onChange(selectedValues.filter(v => v !== option));
                    }
                  }}
                />
                <label 
                  htmlFor={`${field.id}-${option}`}
                  className="text-sm cursor-pointer"
                >
                  {option}
                </label>
              </div>
            ))}
          </div>
        );
      
      case 'date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            min={field.validation_rules?.min_date}
            max={field.validation_rules?.max_date}
          />
        );
      
      case 'boolean':
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              id={field.id}
              checked={value || false}
              onCheckedChange={(checked) => onChange(checked)}
            />
            <label htmlFor={field.id} className="text-sm cursor-pointer">
              {field.help_text || field.field_label}
            </label>
          </div>
        );
      
      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.help_text}
          />
        );
    }
  };
  
  return (
    <div className="space-y-2">
      <Label>
        {field.field_label}
        {field.is_required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {renderField()}
      {field.help_text && !['boolean'].includes(field.field_type) && (
        <p className="text-xs text-muted-foreground">{field.help_text}</p>
      )}
    </div>
  );
}
